import ConfigProvider, { type ThemeConfig } from "antd/lib/config-provider";
import theme from "antd/lib/theme";
import clsx from "clsx";
import { useMemo } from "react";

import { Menu, type MenuProps } from "@sol/uikit/navigation/Menu/Menu";

/**
 * Themed menu variant used for quick-action items in the sidebar.
 * Distinct color scheme from the main menu.
 */
export const SidebarQuickActions = ({ className, items = [], ...props }: MenuProps) => {
    const { token } = theme.useToken();
    const themeConfig: ThemeConfig = useMemo(
        () => ({
            cssVar: { key: "quick-actions-menu" },
            components: {
                Menu: {
                    colorText: token.colorPrimary,
                    groupTitleColor: token.colorPrimaryHover,
                    itemSelectedBg: token.colorPrimaryBorder,
                    itemActiveBg: token.colorPrimaryBorder,
                    itemBg: token.colorPrimaryBgHover,
                },
            },
        }),
        [token],
    );
    return (
        <ConfigProvider theme={themeConfig}>
            <Menu
                className={clsx(
                    "rounded-ant border-r-0! pt-ant-sm [&_.ant-menu-item]:cursor-pointer [&_.ant-menu-submenu-title]:cursor-pointer [&_.ant-menu-title-content]:min-w-0",
                    "[&_.ant-menu-item-group-title]:pl-ant [&_.ant-menu-title-content]:ml-0!",
                    className,
                )}
                items={useMemo(() => {
                    const mapContentMenuItem = (item: (typeof items)[number]): (typeof items)[number] => {
                        if (!item) {
                            return item;
                        }

                        const newItem = {
                            ...item,
                            className: clsx(
                                "[.ant-menu-item-selected]:ring-antd-split ring-1 ring-transparent-base ring-inset before:hidden",
                                item.className,
                            ),
                        };

                        if ("children" in newItem && Array.isArray(newItem.children)) {
                            return {
                                ...newItem,
                                children: newItem.children.map(mapContentMenuItem),
                            };
                        }

                        return newItem;
                    };

                    return items.map(mapContentMenuItem);
                }, [items])}
                {...props}
            />
        </ConfigProvider>
    );
};
